import { useState } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@mailflow/ui';
import { campaignsQuery, useDuplicateCampaign, useDeleteCampaign } from '../lib/campaigns.js';

/*
 * Die Versandart gehoert in die Uebersicht, weil sie darueber entscheidet,
 * wo die Mail beim Empfaenger landet. Vorher stand hier fuer alles ausser
 * A/B-Tests "Regulär" — auch fuer Direktmails, also fuer den Normalfall.
 */
const TYPE_LABELS: Record<string, string> = {
  direct: 'Direkt',
  regular: 'Newsletter',
  ab_test: 'A/B-Test',
  transactional: 'Transaktion',
  journey: 'Journey',
};

const TYPE_HINTS: Record<string, string> = {
  direct: 'Ohne Newsletter-Kopfzeilen — zielt auf den Haupt-Posteingang.',
  regular: 'Mit One-Click-Abmeldeheader nach RFC 8058.',
  ab_test: 'Mehrere Varianten; der Gewinner geht an den Rest.',
  transactional: 'Einzelzustellung, kein Massenversand.',
  journey: 'Aus einem automatisierten Ablauf entstanden.',
};

const STATUS_LABELS: Record<string, string> = {
  draft: 'Entwurf',
  scheduled: 'Geplant',
  sending: 'Wird versendet',
  paused: 'Pausiert',
  completed: 'Abgeschlossen',
  cancelled: 'Abgebrochen',
};

export function CampaignsPage() {
  const navigate = useNavigate();
  const campaigns = useQuery(campaignsQuery);
  const duplicate = useDuplicateCampaign();
  const remove = useDeleteCampaign();

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  const items = campaigns.data?.items ?? [];

  // KPI Statistik-Zahlen
  const totalCount = items.length;
  const activeCount = items.filter((c) => c.status === 'sending' || c.status === 'scheduled').length;
  const draftCount = items.filter((c) => c.status === 'draft').length;
  const completedCount = items.filter((c) => c.status === 'completed').length;

  // Filter-Logik
  const filteredItems = items.filter((item) => {
    const matchesSearch =
      !search.trim() ||
      item.name.toLowerCase().includes(search.toLowerCase()) ||
      item.fromEmail.toLowerCase().includes(search.toLowerCase());
    const matchesStatus = statusFilter === 'all' || item.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  async function handleDuplicate(id: string) {
    const res = await duplicate.mutateAsync(id);
    await navigate({ to: '/kampagnen/$id', params: { id: res.campaign.id } });
  }

  async function handleDelete(id: string, name: string) {
    if (confirm(`Möchtest Du die Kampagne "${name}" wirklich löschen?`)) {
      await remove.mutateAsync(id);
    }
  }

  return (
    <div className="stack" style={{ gap: 'var(--space-6)' }}>
      <header className="page-header">
        <div>
          <h1 style={{ fontSize: 'var(--text-2xl)' }}>Kampagnen</h1>
          <p className="muted text-sm">
            E-Mail-Kampagnen verwalten, duplizieren, filtern und Leistung auswerten.
          </p>
        </div>
        <Link to="/kampagnen/neu" className="btn" data-variant="primary">
          + Neue Kampagne anlegen
        </Link>
      </header>

      {/* KPI DASHBOARD KARTEN */}
      <div className="stat-grid">
        <div className="stat">
          <span className="stat__label">Gesamt Kampagnen</span>
          <span className="stat__value">{totalCount}</span>
        </div>
        <div className="stat">
          <span className="stat__label">Aktiv / Geplant</span>
          <span className="stat__value" style={{ color: 'var(--color-accent)' }}>
            {activeCount}
          </span>
        </div>
        <div className="stat">
          <span className="stat__label">Entwürfe</span>
          <span className="stat__value" style={{ color: 'var(--color-warning)' }}>
            {draftCount}
          </span>
        </div>
        <div className="stat">
          <span className="stat__label">Abgeschlossen</span>
          <span className="stat__value" style={{ color: 'var(--color-success)' }}>
            {completedCount}
          </span>
        </div>
      </div>

      {/* SUCHE & FILTER TOOLBAR */}
      <div
        className="card"
        style={{
          padding: 'var(--space-3)',
          display: 'flex',
          gap: 'var(--space-3)',
          alignItems: 'center',
          flexWrap: 'wrap',
          justifyContent: 'space-between',
        }}
      >
        <div style={{ display: 'flex', gap: 'var(--space-3)', flex: 1, minWidth: '280px' }}>
          <input
            type="search"
            className="input"
            placeholder="🔍 Kampagne suchen …"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ maxWidth: '320px' }}
          />
          <select
            className="input"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            style={{ maxWidth: '200px' }}
          >
            <option value="all">Alle Status-Filter</option>
            <option value="draft">Entwurf</option>
            <option value="scheduled">Geplant</option>
            <option value="sending">Wird versendet</option>
            <option value="paused">Pausiert</option>
            <option value="completed">Abgeschlossen</option>
            <option value="cancelled">Abgebrochen</option>
          </select>
        </div>
        <div className="text-xs muted">
          Zeige {filteredItems.length} von {totalCount} Kampagnen
        </div>
      </div>

      {!campaigns.isLoading && items.length === 0 ? (
        <div className="card">
          <div className="card__body stack" style={{ alignItems: 'flex-start' }}>
            <h2 style={{ fontSize: 'var(--text-lg)' }}>Noch keine Kampagne vorhanden</h2>
            <p className="muted text-sm">
              Erstelle Deine erste E-Mail-Kampagne mit Split-View-Editor, Pre-Flight Spam-Check und Ausschlusslisten.
            </p>
            <Link to="/kampagnen/neu" className="btn" data-variant="primary">
              Erste Kampagne anlegen
            </Link>
          </div>
        </div>
      ) : (
        <div className="card">
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th scope="col">Kampagnen-Name</th>
                  <th scope="col">Status</th>
                  <th scope="col">Versendet</th>
                  <th scope="col">Geöffnet</th>
                  <th scope="col">Geklickt</th>
                  <th scope="col">Geantwortet</th>
                  <th scope="col" style={{ textAlign: 'right' }}>Aktionen</th>
                </tr>
              </thead>
              <tbody>
                {filteredItems.map((campaign) => {
                  const s = campaign.stats ?? { sent: 0, opens: 0, clicks: 0, replies: 0 };
                  return (
                    <tr key={campaign.id}>
                      <td>
                        <Link to="/kampagnen/$id" params={{ id: campaign.id }}>
                          <strong>{campaign.name}</strong>
                        </Link>
                        <span
                          className="badge"
                          {...(campaign.type === 'ab_test' ? { 'data-tone': 'warning' } : {})}
                          style={{ marginLeft: 'var(--space-2)' }}
                          title={TYPE_HINTS[campaign.type] ?? ''}
                        >
                          {TYPE_LABELS[campaign.type] ?? campaign.type}
                        </span>
                      </td>
                      <td>
                        <span className="badge" data-campaign-status={campaign.status}>
                          {STATUS_LABELS[campaign.status] ?? campaign.status}
                        </span>
                      </td>
                      <td>
                        <Link to="/kampagnen/$id" params={{ id: campaign.id }}>
                          <strong>{s.sent}</strong>
                        </Link>
                      </td>
                      <td>
                        <Link to="/kampagnen/$id" params={{ id: campaign.id }}>
                          <span style={{ color: 'var(--color-success)' }}>{s.opens}</span>
                        </Link>
                      </td>
                      <td>
                        <Link to="/kampagnen/$id" params={{ id: campaign.id }}>
                          <span style={{ color: 'var(--color-accent)' }}>{s.clicks}</span>
                        </Link>
                      </td>
                      <td>
                        <Link to="/kampagnen/$id" params={{ id: campaign.id }}>
                          <span>{s.replies}</span>
                        </Link>
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        <div className="cluster" style={{ justifyContent: 'flex-end', gap: 'var(--space-2)' }}>
                          <Button
                            variant="ghost"
                            onClick={() => handleDuplicate(campaign.id)}
                            loading={duplicate.isPending}
                            title="Kampagne als neuen Entwurf duplizieren"
                          >
                            📋 Duplizieren
                          </Button>
                          {(campaign.status === 'draft' || campaign.status === 'scheduled') && (
                            <Link to="/kampagnen/$id/bearbeiten" params={{ id: campaign.id }} className="btn" data-variant="ghost">
                              ✏️ Bearbeiten
                            </Link>
                          )}
                          <Button
                            variant="danger"
                            onClick={() => handleDelete(campaign.id, campaign.name)}
                            loading={remove.isPending}
                            title="Kampagne löschen"
                          >
                            🗑️ Löschen
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
